import { projects } from '../components/Projects';
import { skills, toolings } from '../components/SKill';
import { researchPapers } from '../components/Research';

class UnifiedDataService {
  constructor() {
    this.projects = projects;
    this.skills = skills;
    this.toolings = toolings;
    this.research = researchPapers;
  }

  /**
   * Get context for AI based only on imported portfolio data.
   * Sources: Projects.js, SKill.js, Research.js.
   */
  getContextForAI(query = '') {
    const q = query.toLowerCase();
    const sections = [this._getBaseContext()];

    if (this._matchesKeywords(q, ['project', 'portfolio', 'work', 'built', 'system', 'backend', 'frontend'])) {
      sections.push(this._getProjectsContext());
    }

    if (this._matchesKeywords(q, ['skill', 'tech', 'technology', 'tool', 'stack', 'language', 'framework', 'database', 'devops'])) {
      sections.push(this._getSkillsContext());
      sections.push(this._getToolingsContext());
    }

    if (this._matchesKeywords(q, ['research', 'paper', 'article', 'publication', 'academic'])) {
      sections.push(this._getResearchContext());
    }

    return this._dedupeSections(sections).filter(Boolean).join('\n\n');
  }

  /**
   * Quick answers must also come from imported data only.
   */
  getQuickAnswer(query) {
    return {
      hasAnswer: false,
      context: this.getContextForAI(query),
    };
  }

  /**
   * Get statistics about imported portfolio data only.
   */
  getStats() {
    return {
      projects: this.projects.length,
      research: this.research.length,
      skillCategories: Object.keys(this.skills).length,
      primarySkills: this._flattenSkills().length,
      toolingSections: this.toolings.length,
      toolingItems: this._flattenToolings().length,
    };
  }

  // ============ PRIVATE HELPER METHODS ============

  _matchesKeywords(query, keywords) {
    return keywords.some((keyword) => query.includes(keyword));
  }

  _dedupeSections(sections) {
    return [...new Set(sections)];
  }

  _flattenSkills() {
    return Object.entries(this.skills).flatMap(([category, items]) =>
      items.map((skill) => ({
        category,
        name: skill.name,
        level: skill.level,
      }))
    );
  }

  _flattenToolings() {
    return this.toolings.flatMap((section) =>
      (section.programmingLanguages || []).map((tool) => ({
        section: section.title,
        name: tool.name,
        level: tool.level,
      }))
    );
  }

  _getBaseContext() {
    return [
      'IMPORTED PORTFOLIO DATA ONLY',
      'Use only the data listed in these sections. If a question asks for anything outside these sections, say that information is not available in the imported portfolio data.',
      this._getSkillsContext(),
      this._getToolingsContext(),
      this._getProjectsSummaryContext(),
      this._getResearchContext(),
    ].join('\n\n');
  }

  _getSkillsContext() {
    const skillLines = this._flattenSkills().map(
      (skill) => `- ${skill.category}: ${skill.name} (${skill.level}%)`
    );

    return `PRIMARY SKILLS:\n${skillLines.join('\n')}`;
  }

  _getToolingsContext() {
    const toolingLines = this.toolings.map((section) => {
      const tools = (section.programmingLanguages || [])
        .map((tool) => `${tool.name} (${tool.level}%)`)
        .join(', ');

      return `- ${section.title}: ${tools}`;
    });

    return `TECH STACK:\n${toolingLines.join('\n')}`;
  }

  _getProjectsSummaryContext() {
    const projectLines = this.projects.map((project) => {
      const tech = Array.isArray(project.tech) ? project.tech.join(', ') : '';
      return `- ${project.title}: ${project.category}. Tech: ${tech}`;
    });

    return `PROJECTS SUMMARY (${this.projects.length} total):\n${projectLines.join('\n')}`;
  }

  _getProjectsContext() {
    const projectLines = this.projects.map((project) => {
      const tech = Array.isArray(project.tech) ? project.tech.join(', ') : '';

      return [
        `- ${project.title}`,
        `  Category: ${project.category}`,
        `  Description: ${project.description}`,
        `  Impact: ${project.impact}`,
        `  Tech: ${tech}`,
      ].join('\n');
    });

    return `PROJECT DETAILS:\n${projectLines.join('\n')}`;
  }

  _getResearchContext() {
    const researchLines = this.research.map((paper) => {
      const pdfStatus = paper.pdfUrl ? `PDF: ${paper.pdfUrl}` : 'PDF: not available';
      return `- ${paper.title}. Impact: ${paper.expectedImpact}. ${pdfStatus}`;
    });

    return `RESEARCH PAPERS (${this.research.length} total):\n${researchLines.join('\n')}`;
  }
}

// Export singleton instance
const unifiedDataService = new UnifiedDataService();
export default unifiedDataService;
